import { PhotoIcon } from "@heroicons/react/24/solid";
import { SellerOrderItem as SellerOrderItemType } from '@shopify-multiseller/graphql/partner/types';
import { Link } from "react-router-dom";
import { formatWeight } from "../../helpers/formatters";
import { Badge } from "../../components/primitives";

/**
 * A line of the order being packed: what it is, what it weighs, how many.
 *
 * The three-column grid inside a two-column grid it replaces stacked the
 * product name, its option badge and its SKU vertically, so a two-line item
 * occupied about 90px of a screen whose whole job is a short checklist.
 */
export const SellerOrderItem = ({ item }: { item: SellerOrderItemType }) => {
    const options = item.productVariantOptions?.map((option) => option.value).join(" / ") || undefined;
    const thumbnail = item.featuredImage?.thumbnailUrl ?? item.productThumbnail ?? undefined;

    return (
        <div className="flex items-start gap-3 border-b border-border/60 py-3.5 first:pt-0 last:border-b-0 last:pb-0">
            {thumbnail ? (
                <img
                    src={thumbnail}
                    alt={item.featuredImage?.altText ?? ''}
                    className="h-11 w-11 shrink-0 rounded border border-border object-cover"
                />
            ) : (
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-border bg-muted/40">
                    <PhotoIcon className="h-5 w-5 text-muted-foreground/50" aria-hidden="true" />
                </div>
            )}

            <div className="min-w-0 flex-1">
                <Link
                    to={`/products/${item.productId}`}
                    className="text-sm font-medium text-primary hover:underline"
                >
                    {item.productIdAlias} · {item.title}
                </Link>
                <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {options && <Badge variant="neutral">{options}</Badge>}
                    {item.sku && <span>SKU {item.sku}</span>}
                </p>
            </div>

            {item.weight != null && item.weightUnit && (
                <span className="shrink-0 text-xs text-muted-foreground">
                    {formatWeight(item.weight, item.weightUnit)}
                </span>
            )}
            <span className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">
                × {item.quantity}
            </span>
        </div>
    );
}
